import { motion } from "framer-motion";
import { Zap, Feather, Droplets, CalendarCheck } from "lucide-react";
import { IMAGES } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

const STEPS = [
  {
    num: "01",
    title: "Applicazione di precisione",
    text: "L'adesivo fotosensibile non si asciuga all'aria: ogni extension viene posizionata sulla ciglia naturale con calma e precisione millimetrica.",
  },
  {
    num: "02",
    title: "Luce UV/LED",
    text: "Una lampada delicata illumina il punto di applicazione per 1-3 secondi. Il momento esatto dell'indurimento lo decidiamo noi, non l'aria.",
  },
  {
    num: "03",
    title: "Sigillo istantaneo",
    text: "La luce avvia la polimerizzazione: la colla si solidifica subito e in profondità. Niente attese, niente ciglia che si attaccano tra loro.",
  },
];

const BENEFITS = [
  {
    icon: Zap,
    title: "Tenuta superiore",
    text: "Il legame completo e uniforme fa durare le extension fino a 4-6 settimane: refill consigliato ogni 3-4.",
  },
  {
    icon: Feather,
    title: "Più delicate",
    text: "La polimerizzazione immediata riduce i vapori della colla: ideale anche per gli occhi più sensibili.",
  },
  {
    icon: Droplets,
    title: "Immuni all'umidità",
    text: "La colla tradizionale dipende da umidità e temperatura. Quella UV no: risultato impeccabile tutto l'anno.",
  },
  {
    icon: CalendarCheck,
    title: "Zero attese",
    text: "Subito dopo il trattamento puoi bagnare il viso e tornare alla tua routine: il sigillo è già completo.",
  },
];

const STATS = [
  { value: "1-3 sec", label: "Polimerizzazione istantanea" },
  { value: "4-6 sett.", label: "Durata delle extension" },
  { value: "3-4 sett.", label: "Intervallo consigliato per il refill" },
];

export default function UVMethod() {
  return (
    <section id="metodo-uv" data-testid="uv-method-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-6 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-blush-500"
          >
            <span className="h-px w-10 bg-blush-500" />
            Il metodo UV
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-serif text-4xl leading-tight text-ink sm:text-5xl lg:text-6xl"
          >
            La rivoluzione delle <span className="italic text-blush-500">extension ciglia.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ink-soft"
          >
            Da Lash Queen le extension si applicano con la tecnica UV: al posto
            della colla tradizionale usiamo un adesivo fotosensibile che resta
            lavorabile finché non viene colpito dalla luce di una speciale
            lampada UV/LED. Il risultato? Più controllo in fase di applicazione,
            più durata e più delicatezza sui tuoi occhi.
          </motion.p>

          <div className="mt-14 space-y-10">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.num}
                data-testid={`uv-step-${s.num}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.1 }}
                className="flex gap-6"
              >
                <span className="font-serif text-4xl italic leading-none text-blush-500">{s.num}</span>
                <div>
                  <h3 className="font-serif text-xl italic text-ink lg:text-2xl">{s.title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-ink-soft">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="relative lg:sticky lg:top-32"
          >
            <div aria-hidden className="absolute -inset-4 rounded-t-full bg-blush-50" />
            <div className="relative overflow-hidden rounded-t-full" style={{ aspectRatio: "3/4" }}>
              <img
                src={IMAGES.volume}
                alt="Extension ciglia con tecnica UV realizzate da Lash Queen"
                data-testid="uv-method-image"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
              />
            </div>
            <div
              data-testid="uv-method-badge"
              className="absolute -bottom-5 right-6 rounded-full border border-blush-100 bg-cream/90 px-6 py-3 shadow-xl shadow-blush-100/60 backdrop-blur-md"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-blush-600">
                Tecnica UV · Senza attese
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mt-24 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {BENEFITS.map((b, i) => (
          <motion.div
            key={b.title}
            data-testid={`uv-benefit-${i}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
            className="rounded-2xl border border-blush-100 bg-cream p-8 transition-shadow duration-500 hover:shadow-xl hover:shadow-blush-100/70"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blush-50 text-blush-600">
              <b.icon size={18} />
            </span>
            <h3 className="mt-6 font-serif text-xl italic text-ink">{b.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-ink-soft">{b.text}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        data-testid="uv-stats-band"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mt-24 grid grid-cols-1 gap-10 border-y border-blush-100 py-14 sm:grid-cols-3"
      >
        {STATS.map((s, i) => (
          <div key={s.label} data-testid={`uv-stat-${i}`} className="text-center">
            <p className="font-serif text-5xl italic text-blush-500 lg:text-6xl">{s.value}</p>
            <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-ink-soft">{s.label}</p>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
